import { format } from "date-fns";
import {
	Archive,
	BookOpen,
	Bug,
	EyeOff,
	type LucideIcon,
	RefreshCw,
	ShieldCheck,
	Sparkles,
	TrendingUp,
	Zap,
} from "lucide-react";
import {
	type ChangelogVersion,
	ENTRY_TYPE_META,
	type EntryType,
} from "~/data/changelog-schema";
import { cn } from "~/utils/cn";

const TYPE_ICON: Record<EntryType, LucideIcon> = {
	feature: Sparkles,
	improvement: TrendingUp,
	fix: Bug,
	performance: Zap,
	security: ShieldCheck,
	change: RefreshCw,
	deprecated: Archive,
	docs: BookOpen,
};

/** Format an ISO date defensively (falls back to the raw string). */
function formatDate(iso: string): string {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return iso;
	}
	return format(parsed, "MMM d, yyyy");
}

/** Drop the leading `MAJOR.MINOR.PATCH` so RC labels read as `rc.3`. */
function shortTag(tag: string, version: string): string {
	if (tag === version) {
		return "Stable";
	}
	const suffix = tag.split("-").slice(1).join("-");
	return suffix || tag;
}

const EntryRow = ({
	type,
	title,
	body,
	internal,
	delayMs,
}: {
	type: EntryType;
	title: string;
	body: string;
	internal: boolean;
	delayMs: number;
}) => {
	const meta = ENTRY_TYPE_META[type];
	const Icon = TYPE_ICON[type];
	return (
		<div className="flex gap-3.5">
			<div className="flex w-3 flex-none justify-center">
				<span
					className="relative mt-[7px] h-3 w-3 rounded-full border-2 border-background"
					style={{
						backgroundColor: meta.dot,
						boxShadow: `0 0 0 3px ${meta.dot}1f`,
					}}
				/>
			</div>
			<article
				className="group fade-in slide-in-from-bottom-1 mb-4 flex-1 animate-in rounded-xl border border-border/60 bg-card/60 p-3.5 transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-sm"
				style={{ animationDelay: `${delayMs}ms`, animationFillMode: "both" }}
			>
				<div className="mb-1.5 flex items-center gap-2">
					<span
						className={cn(
							"inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold text-[11px] leading-none",
							meta.badge
						)}
					>
						<Icon className="h-3 w-3" />
						{meta.label}
					</span>
					{internal && (
						<span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 font-semibold text-[10px] text-amber-600 uppercase leading-none tracking-wide ring-1 ring-amber-500/20 dark:text-amber-400">
							<EyeOff className="h-2.5 w-2.5" />
							Internal
						</span>
					)}
				</div>
				<h4 className="font-semibold text-[14px] text-foreground leading-snug">
					{title}
				</h4>
				{body && (
					<p className="mt-1 text-[13px] text-muted-foreground leading-relaxed">
						{body}
					</p>
				)}
			</article>
		</div>
	);
};

interface ChangelogTimelineProps {
	version: ChangelogVersion;
	showInternal?: boolean;
}

export const ChangelogTimeline = ({
	version,
	showInternal = false,
}: ChangelogTimelineProps) => {
	let entryIndex = 0;
	const releasesWithEntries = version.releases
		.map((release) => ({
			...release,
			visibleEntries: release.entries.filter(
				(entry) => showInternal || entry.visibility === "public"
			),
		}))
		.filter((release) => release.visibleEntries.length > 0);

	if (releasesWithEntries.length === 0) {
		return (
			<p className="px-1 py-8 text-center text-muted-foreground text-sm">
				No notes published for this version yet.
			</p>
		);
	}

	return (
		<div className="relative flex flex-col">
			{/* One continuous spine behind every dot (so it never breaks between sections). */}
			<span
				aria-hidden
				className="absolute top-2 bottom-3 left-[6px] w-px -translate-x-1/2 bg-border/70"
			/>
			{releasesWithEntries.map((release) => (
				<section className="mb-2" key={release.tag}>
					<div className="flex gap-3.5">
						<div className="flex w-3 flex-none justify-center">
							<span className="relative mt-1 h-3 w-3 rounded-full border-2 border-background bg-primary ring-4 ring-primary/15" />
						</div>
						<div className="mb-3 flex flex-1 items-baseline justify-between gap-2">
							<span className="font-semibold text-foreground text-sm">
								{shortTag(release.tag, version.version)}
							</span>
							<time className="text-[12px] text-muted-foreground tabular-nums">
								{formatDate(release.date)}
							</time>
						</div>
					</div>
					{release.visibleEntries.map((entry) => {
						const delay = entryIndex * 35;
						entryIndex += 1;
						return (
							<EntryRow
								body={entry.body}
								delayMs={delay}
								internal={entry.visibility === "internal"}
								key={entry.id}
								title={entry.title}
								type={entry.type}
							/>
						);
					})}
				</section>
			))}
		</div>
	);
};
